import { Checkbox, Empty, Input, Key, Select, Tooltip, Tree } from '@maxtropy/components';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { EnergyAnalysisRankingContext } from '../..';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyAnalysisListByOuPost,
  apiV2EnergyAnalysisTreePost,
  V2EnergyAnalysisTreePostResponse,
  apiV2EnergyUnitGetGroupListPost,
} from '@maxtropy/device-customer-apis-v2';
import { Divider, Spin } from 'antd';
import { LockFilled } from '@ant-design/icons';
import { DataNode } from 'antd/es/tree';
import { generateList, getParentKey, initExpandedKeys, NumToChinese, NumToChineseDisplay } from '../../utils';
import StatusButton from './StatusButton';

const { Search } = Input;

export type AnalysisItem = Exclude<V2EnergyAnalysisTreePostResponse['list'], undefined>[number];

function getMaxDepth(tree: AnalysisItem[]): number {
  if (!tree || tree.length === 0) return 0;

  let max = 0;
  for (const node of tree) {
    const depth = 1 + getMaxDepth(node.children || []);
    max = Math.max(max, depth);
  }
  return max;
}

// 获取指定层级的所有节点key
const getLevelKeys = (tree: AnalysisItem[], level: number, currentLevel = 1): Key[] => {
  let keys: Key[] = [];

  if (currentLevel === level) {
    // 当前层级，收集所有节点（非禁用的）
    tree.forEach(item => {
      if (!item.disabled) {
        keys.push(item.energyUnitId!);
      }
    });
    return keys;
  }

  // 还未到达目标层级，继续向下遍历
  tree.forEach(item => {
    if (item.children && item.children.length > 0) {
      keys = keys.concat(getLevelKeys(item.children, level, currentLevel + 1));
    }
  });

  return keys;
};

// 使用unitTreeData查找原始数据，而不是过滤后的treeData
const findNodeInOriginalData = (nodes: AnalysisItem[] | undefined, key: React.Key): AnalysisItem | undefined => {
  if (!nodes) return undefined;
  for (const node of nodes) {
    if (node.energyUnitId === key) {
      return node;
    }
    if (node.children) {
      const found = findNodeInOriginalData(node.children, key);
      if (found) return found;
    }
  }
  return undefined;
};

// 递归获取所有子节点ID（基于原始数据）
const getAllChildIds = (node: AnalysisItem): Key[] => {
  let ids: Key[] = [];
  if (node.children) {
    node.children.forEach(child => {
      if (!child.disabled) {
        ids.push(child.energyUnitId!);
      }
      ids = ids.concat(getAllChildIds(child));
    });
  }
  return ids;
};

const EnergyTree = () => {
  const { unitIds, setUnitIds, groupId, setGroupId } = React.useContext(EnergyAnalysisRankingContext);
  const [searchValue, setSearchValue] = useState<string>(''); // 搜索输入框
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]); // 左侧树展开的节点
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([]); // 选中的节点
  const [dataList, setDataList] = useState<{ key: React.Key; title: string }[]>([]); // 树形节点平铺列表
  const [selectAll, setSelectAll] = useState<boolean>(); // 全选
  const [maxDepth, setMaxDepth] = useState<number>(0);
  const [selectedLevel, setSelectedLevel] = useState<number>(1);
  const [intoGroup, setIntoGroup] = useState<string | undefined>(undefined); // 搜索输入框

  // 获取用能分析组
  const { data: groupOptions } = useRequest(() =>
    apiV2EnergyAnalysisListByOuPost({}).then(res => {
      return (res.list ?? []).map(item => ({ label: item.name as string, value: item.id as number }));
    })
  );

  // 获取分组
  const { data: groupingoptions } = useRequest(() =>
    apiV2EnergyUnitGetGroupListPost({}).then(res => {
      return res.list ?? [];
    })
  );

  useEffect(() => {
    if (!intoGroup) return;
    // 有值的话树对应的值也要对应的上
  }, [intoGroup]);

  // 默认选择第一个用能分析组
  useEffect(() => {
    if ((groupOptions ?? []).length > 0) {
      setUnitIds?.(undefined);
      setGroupId?.(groupOptions?.[0].value!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupOptions]);

  // 获取用能单元树形结构
  const { data: unitTreeData, loading } = useRequest(
    () =>
      apiV2EnergyAnalysisTreePost({
        energyUnitGroupId: String(groupId),
      }).then(res => {
        setSearchValue('');
        const tempDataList = generateList((res.list ?? []) as AnalysisItem[]);
        setDataList(tempDataList);
        return res.list ?? [];
      }),
    {
      refreshDeps: [groupId],
      ready: !!groupId,
    }
  );

  // 用能分析总览选择一级用能分析单元
  useEffect(() => {
    if (unitTreeData) {
      const selectedUnitIds = unitTreeData.filter(c => !c.disabled).map(item => item.energyUnitId!);
      setSelectAll(true);
      setSelectedLevel(1);
      setCheckedKeys(selectedUnitIds);
      setUnitIds?.(selectedUnitIds);
    }
    setMaxDepth(getMaxDepth(unitTreeData ?? []));
    setExpandedKeys(initExpandedKeys(unitTreeData ?? []));
  }, [unitTreeData]);

  useEffect(() => {
    Array.from({ length: maxDepth }, (_, i) => ({
      title: `${NumToChineseDisplay[(i + 1) as NumToChinese]}级用能单元`,
    }));
  }, [maxDepth]);

  const checkAllChildren = (data: DataNode, checked: boolean) => {
    // 从原始数据中获取节点
    const originalNode = findNodeInOriginalData(unitTreeData, data.key);
    if (!originalNode) return;

    const unitIds = getAllChildIds(originalNode);
    let selectedKeys;
    if (checked) {
      selectedKeys = [...checkedKeys, ...unitIds];
      setExpandedKeys(Array.from(new Set([data.key, ...selectedKeys, ...expandedKeys])));
    } else {
      selectedKeys = checkedKeys.filter(key => !unitIds.includes(key));
    }
    setCheckedKeys(selectedKeys);
    setUnitIds?.(selectedKeys);
  };

  const titleRender = (item: DataNode) => {
    let checked = false;

    // 检查子节点是否都选中
    if (item.children && item.children.length > 0) {
      checked = item.children.every(child => checkedKeys.includes(child.key));
    }

    return (
      <div>
        {item.title as React.ReactNode}
        {item.children && item.children.length > 0 && (
          <StatusButton checked={checked} onChange={(_, value) => checkAllChildren(item, value)}>
            全选子节点
          </StatusButton>
        )}
      </div>
    );
  };

  // tree数据生成
  const treeData = useMemo(() => {
    const loop = (data: AnalysisItem[]): DataNode[] =>
      (data ?? [])
        .map(item => {
          const strTitle = item.name as string;
          const index = strTitle.indexOf(searchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + searchValue.length);
          const disableIcon = <LockFilled style={{ color: 'var(--mx-warning-color)', fontSize: 12, marginLeft: 4 }} />;
          let title = <span>{strTitle}</span>;
          if (index > -1) {
            if (item.disabled) {
              title = (
                <Tooltip title="无数据权限" key={item.energyUnitId}>
                  <span>
                    {beforeStr}
                    <span className={styles.highLight}>{searchValue}</span>
                    {afterStr}
                    {disableIcon}
                  </span>
                </Tooltip>
              );
            } else {
              title = (
                <span>
                  {beforeStr}
                  <span className={styles.highLight}>{searchValue}</span>
                  {afterStr}
                </span>
              );
            }
          } else {
            if (item.disabled) {
              title = (
                <Tooltip title="无数据权限" key={item.energyUnitId}>
                  <span>
                    {strTitle}
                    {disableIcon}
                  </span>
                </Tooltip>
              );
            } else {
              title = <span>{strTitle}</span>;
            }
          }

          if (item.children && item.children.length > 0) {
            const children = loop(item.children);

            return {
              title: index > -1 || children.length ? title : null,
              key: item.energyUnitId ?? '',
              disabled: item.disabled,
              children: children,
            };
          }

          return index > -1
            ? {
                key: item.energyUnitId ?? '',
                disabled: item.disabled,
                title,
              }
            : {
                key: item.energyUnitId ?? '',
                disabled: item.disabled,
                title: null,
              };
        })
        .filter(item => !!item.title);
    return loop(unitTreeData!);
  }, [searchValue, unitTreeData]);

  // 处理全选/取消全选逻辑
  useEffect(() => {
    if (!unitTreeData) return;

    let keysToSelect: Key[] = [];

    if (selectAll) {
      // 根据选择的层级来确定要选中的节点
      if (selectedLevel === 0) {
        // 全部用能单元
        const loop = (data: AnalysisItem[]): Key[] => {
          let keys: Key[] = [];
          data.forEach(item => {
            if (!item.disabled) {
              keys.push(item.energyUnitId!);
            }
            if (item.children && item.children.length > 0) {
              keys = keys.concat(loop(item.children));
            }
          });
          return keys;
        };
        keysToSelect = loop(unitTreeData);
      } else {
        // 指定层级用能单元
        keysToSelect = getLevelKeys(unitTreeData, selectedLevel);
      }
    }

    setCheckedKeys(keysToSelect);
    setUnitIds?.(keysToSelect);
  }, [selectAll, selectedLevel, unitTreeData]);

  // 设置展开节点
  // useEffect(() => {
  //     const defaultExpandedKeys = initExpandedKeys(treeData);
  //     setExpandedKeys(defaultExpandedKeys);

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [treeData]);

  // 展开节点
  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys([...newExpandedKeys]);
  };

  // 用能单元搜索框
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value) {
      const newExpandedKeys = dataList
        .map(item => {
          if (item.title.indexOf(value) > -1) {
            return getParentKey(item.key, unitTreeData);
          }
          return null;
        })
        .filter((item, i, self) => item && self.indexOf(item) === i);
      setExpandedKeys(Array.from(new Set([...expandedKeys, ...(newExpandedKeys as React.Key[])])));
    }
    setSearchValue(value);
  };

  const onCheck = (keys: any, info: any) => {
    let newKeys;
    if (Array.isArray(keys)) {
      newKeys = keys;
    } else {
      newKeys = keys?.checked;
    }
    setCheckedKeys(newKeys);
    setUnitIds?.(newKeys);
  };

  return (
    <div className={styles.leftSider}>
      <div className={styles.filterArea}>
        <div className={styles.option}>用能分析组：</div>
        <Select
          placeholder="请选择"
          className={styles.selectBox}
          value={groupId}
          allowClear={false}
          options={groupOptions}
          onChange={val => {
            setUnitIds?.([]);
            setCheckedKeys([]);
            setIntoGroup(undefined);
            setSelectAll(false);
            setGroupId?.(val);
          }}
        />
      </div>
      <div className={styles.searchArea}>
        <Search placeholder="请输入用能单元名称" value={searchValue} allowClear onChange={onChange} />
      </div>
      <div className={styles.grouping}>
        <span>分组：</span>
        <Select
          placeholder="请选择"
          className={styles.selectBox}
          value={intoGroup}
          allowClear={false}
          options={groupingoptions?.map(item => ({ label: item.groupName, value: item.id }))}
          onChange={val => {
            setIntoGroup(val);
            const findEnergy = groupingoptions?.find(i => i.id === Number(val));
            const Ids = findEnergy?.energyUnitList?.map(u => u.energyUnitId);
            setUnitIds?.(Ids as Key[]);
            setCheckedKeys(Ids as Key[]);
            // setSelectAll(false);
            setGroupId?.(findEnergy?.energyUnitGroupId!);
          }}
        />
      </div>
      <div className={styles.splitLine}>
        <Divider className={styles.divider} />
      </div>
      <div className={styles.treeArea}>
        <Spin spinning={loading}>
          {treeData && treeData.length > 0 ? (
            <>
              <div className={styles.checkArea}>
                <Checkbox
                  checked={selectAll}
                  className={styles.allselectbox}
                  onChange={e => setSelectAll(e.target.checked)}
                >
                  全选
                </Checkbox>
                <Select
                  style={{ flex: 1, marginRight: 10 }}
                  size="small"
                  value={selectedLevel}
                  onChange={setSelectedLevel}
                >
                  <Select.Option value={0}>全部用能单元</Select.Option>
                  {Array.from({ length: maxDepth }, (_, i) => (
                    <Select.Option key={i} value={i + 1}>
                      {NumToChineseDisplay[(i + 1) as NumToChinese]}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className={styles.tree_box}>
                <Tree
                  // showLine={{ showLeafIcon: false }} // 叶子节点也保留占位
                  checkable
                  checkStrictly={true}
                  className={styles.treeStyle}
                  showIcon
                  blockNode
                  treeData={treeData}
                  checkedKeys={checkedKeys}
                  onExpand={onExpand}
                  expandedKeys={expandedKeys}
                  onCheck={onCheck}
                  titleRender={titleRender}
                />
              </div>
            </>
          ) : (
            <Empty style={{ paddingTop: 100 }} />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default EnergyTree;
